import type { SxProps, Theme } from "@mui/material";
import { Box, Skeleton } from "@mui/material";
import type { FC } from "react";
import { CONTROL_HEIGHT, NEUTRAL } from "../../../theme/tokens";
import { SystemSwitch } from "../Clickable/SystemSwitch";
import { SystemChips } from "../Clickable/TabsAndChips/SystemChips/SystemChips";
import { DateInput } from "../Inputs/DateInput";
import { StaffAutoComplete } from "../Inputs/StaffAutoComplete";
import { SystemDropdown } from "../Inputs/SystemDropDown";
import { DateRangeSelector } from "../molecules/DateRangeSelector";
import { FacetFilterSection } from "./FacetFilterSection";
import { FilterFieldGroup } from "./FilterFieldGroup";
import { FilterFieldRow } from "./FilterFieldRow";
import { type ChipsFilterField, type CustomFilterField, type FilterField, type FilterSection, isVisible } from "./filterFields";

/** Default gap between fields and between sections — the rail's own field rhythm. */
const DEFAULT_GAP = 2;
/** `DateRangeSelector`'s rendered width (two date inputs + separator), for its skeleton. */
const DATE_RANGE_SKELETON_WIDTH = 260;
/** A labeled `SystemSwitch`'s rendered width (toggle + label), for its skeleton. */
const SWITCH_SKELETON = { width: 96, height: 24, borderRadius: 11 } as const;
/** Chip-row skeleton: a short label line above three pills of plausible, uneven widths. */
const CHIP_SKELETON = { labelWidth: 60, labelHeight: 12, height: 28, borderRadius: 4, widths: [56, 64, 52] } as const;
/** Facet-group skeleton: a title line above a few option rows. */
const FACET_SKELETON = { titleWidth: 80, titleHeight: 12, rowHeight: 20, rows: 4, rowWidths: ["70%", "85%", "60%", "78%"] } as const;

/** Every field's test id derives from its own `id` unless it overrides one, so ids can't drift from config. */
const fieldTestId = (field: FilterField, dataTestId?: string): string | undefined => field.dataTestId ?? (dataTestId ? `${dataTestId}-${field.id}` : undefined);

/**
 * The real control for one field.
 *
 * Every branch resolves to an existing UIToolkit primitive — this component owns the mapping from
 * config to primitive and nothing else, so a control's own behavior stays the primitive's business.
 */
const FilterFieldControl: FC<{ field: FilterField; dataTestId?: string }> = ({ field, dataTestId }) => {
	const testId = fieldTestId(field, dataTestId);

	switch (field.kind) {
		case "facet":
			return (
				<FacetFilterSection
					title={field.title}
					options={field.options}
					onToggle={field.onToggle}
					defaultExpanded={field.defaultExpanded}
					expanded={field.expanded}
					visibleLimit={field.visibleLimit}
					countMode={field.countMode}
					foldZeroCounts={field.foldZeroCounts}
					searchable={field.searchable}
					searchPlaceholder={field.searchPlaceholder}
					highlighted={field.highlighted}
					onExpandedChange={field.onExpandedChange}
					onShowAllChange={field.onShowAllChange}
					dataTestId={testId}
				/>
			);

		case "select":
			// `label` goes to the dropdown's own floating label, so this field never takes a
			// FilterFieldRow micro-label — only its optional width pin.
			return (
				<FilterFieldRow width={field.width}>
					{field.multiple ? (
						<SystemDropdown
							label={field.label}
							multiple
							loading={field.loading}
							items={field.items}
							selectedItems={field.value}
							onChangeMultiple={field.onChange}
							// DEFAULT: clearable — a filter the user cannot unset is a trap, so opting OUT is the
							// deliberate choice, not opting in.
							clearable={field.clearable ?? true}
							placeholder={field.placeholder}
							selectSx={{ width: "100%" }}
							data-testid={testId}
						/>
					) : (
						<SystemDropdown
							label={field.label}
							loading={field.loading}
							items={field.items}
							selectedItem={field.value}
							onChange={field.onChange}
							// DEFAULT: see the multi-select branch above.
							clearable={field.clearable ?? true}
							placeholder={field.placeholder}
							selectSx={{ width: "100%" }}
							data-testid={testId}
						/>
					)}
				</FilterFieldRow>
			);

		case "chips":
			// Chips carry no label of their own, so this is where the micro-label earns its place.
			return (
				<FilterFieldRow label={field.label} width={field.width}>
					<SystemChips items={field.items} selectedItems={field.selected} onChange={field.onChange} forceFitContent dataTestId={testId} />
				</FilterFieldRow>
			);

		case "switch": {
			const control = (
				<SystemSwitch
					checked={field.checked}
					onChange={(event) => field.onChange(event.target.checked)}
					label={field.label}
					disabled={field.disabled}
					tooltip={field.tooltip}
					dataTestId={testId}
				/>
			);
			// The adornment is decorative and absolutely positioned, so it needs a positioned parent —
			// added only when there is one, to keep the common switch a single node.
			return field.adornment ? (
				<Box sx={{ position: "relative" }}>
					{control}
					{field.adornment}
				</Box>
			) : (
				control
			);
		}

		case "date":
			return (
				<DateInput
					value={field.value}
					onChange={field.onChange}
					// DEFAULT: clearable — same reasoning as the select above.
					onClear={(field.clearable ?? true) ? () => field.onChange(undefined) : undefined}
					// Reserves the clear icon's width before a date is picked, so gaining one doesn't shift the row.
					reserveClearWidth={field.clearable ?? true}
					minDate={field.minDate}
					maxDate={field.maxDate}
					label={field.label}
					formControlSx={field.width === undefined ? { width: "100%" } : { width: field.width }}
					dataTestId={testId}
				/>
			);

		case "dateRange":
			// Presented flush: the rail already frames its contents, so the selector's own card
			// background, border and padding would draw a second box inside the first.
			return (
				<FilterFieldRow width={field.width} dataTestId={testId}>
					<DateRangeSelector
						startDate={field.startDate}
						endDate={field.endDate}
						onStartDateChange={field.onStartDateChange}
						onEndDateChange={field.onEndDateChange}
						// A rail is a narrow column: at the standard size the two pickers overflow it and wrap,
						// so they render dense and share one row instead.
						compact
						removeBackground
						removeBorder
						padding={0}
					/>
				</FilterFieldRow>
			);

		case "staff":
			return (
				<FilterFieldRow width={field.width} dataTestId={testId}>
					<StaffAutoComplete nurses={field.nurses} value={field.value} onChange={field.onChange} fullWidth label={field.label} />
				</FilterFieldRow>
			);

		case "custom":
			return (
				<FilterFieldRow label={field.label} width={field.width} dataTestId={testId}>
					{field.render()}
				</FilterFieldRow>
			);
	}
};

/**
 * The shape standing in for one field's control while it loads.
 *
 * Static text is NOT skeletonized — a field's own label and its group's title are in the config, not
 * in the response, so they render for real in both states (`FilterFieldRow` wraps this the same way
 * it wraps the real control). Only what the fetch actually decides gets a placeholder.
 */
const FilterFieldSkeletonShape: FC<{ field: FilterField }> = ({ field }) => {
	switch (field.kind) {
		case "facet":
			return (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
					<Skeleton variant="text" width={FACET_SKELETON.titleWidth} height={FACET_SKELETON.titleHeight} />
					{FACET_SKELETON.rowWidths.slice(0, FACET_SKELETON.rows).map((width) => (
						<Skeleton key={width} variant="text" width={width} height={FACET_SKELETON.rowHeight} />
					))}
				</Box>
			);

		case "chips":
			return (
				<Box sx={{ display: "flex", gap: 0.5 }}>
					{CHIP_SKELETON.widths.map((width) => (
						<Skeleton key={width} variant="rounded" width={width} height={CHIP_SKELETON.height} sx={{ borderRadius: CHIP_SKELETON.borderRadius }} />
					))}
				</Box>
			);

		case "switch":
			return <Skeleton variant="rounded" width={SWITCH_SKELETON.width} height={SWITCH_SKELETON.height} sx={{ borderRadius: SWITCH_SKELETON.borderRadius }} />;

		case "dateRange":
			return <Skeleton variant="rounded" width={field.width ?? DATE_RANGE_SKELETON_WIDTH} height={CONTROL_HEIGHT.COMPACT} />;

		case "custom":
			return <Skeleton variant="rounded" width={field.width ?? "100%"} height={field.skeletonHeight ?? CONTROL_HEIGHT.COMPACT} />;

		// Every remaining kind renders as a single field-height control (select, date, staff).
		default:
			return <Skeleton variant="rounded" width={field.width ?? "100%"} height={CONTROL_HEIGHT.COMPACT} />;
	}
};

/**
 * Kinds whose label sits OUTSIDE the control, so it survives into the loading state.
 *
 * The rest (`select`, `date`, `staff`) label themselves from inside the control, which is itself a
 * placeholder while loading — so there is no label to keep, matching how those fields already read.
 */
const hasExternalLabel = (field: FilterField): field is ChipsFilterField | CustomFilterField => field.kind === "chips" || field.kind === "custom";

/** One field's placeholder, wrapped in the same labeled row the real control gets. */
const FilterFieldSkeleton: FC<{ field: FilterField }> = ({ field }) => (
	<FilterFieldRow label={hasExternalLabel(field) ? field.label : undefined}>
		<FilterFieldSkeletonShape field={field} />
	</FilterFieldRow>
);

type FilterFieldListProps = {
	sections: FilterSection[];
	/**
	 * Swap every field for a placeholder of its own shape, so the rail holds its layout instead of
	 * collapsing while options load. Derived from `sections`, so it needs no maintenance of its own.
	 */
	loading?: boolean;
	/**
	 * Hairline dividers between sections, matching `FilterSidebar`'s own treatment of its direct
	 * children — for a rail of titled groups (a facet rail) rather than a flat run of controls.
	 *
	 * Turning it on also drops the inter-section gap to 0 by default, because that is what the divider
	 * treatment assumes: the groups carry their own padding and the line does the separating. Pass an
	 * explicit `gap` to override.
	 */
	dividers?: boolean;
	/** Gap between sections (theme spacing units). Defaults to the field rhythm, or 0 with `dividers`. */
	gap?: number;
	/**
	 * Default gap between fields WITHIN each section, overridable per section.
	 *
	 * Worth setting once for a rail of titled sections: each header brings its own vertical padding, so
	 * the per-frame default (tuned for an untitled run) reads too airy under one.
	 */
	fieldGap?: number;
	/**
	 * Vertical breathing room around each section (theme spacing units), as SYMMETRIC padding.
	 *
	 * Padding rather than `gap`, specifically because of `dividers`: a gap puts all of its space on one
	 * side of the hairline, so the line ends up tight against the next section's title and reads as
	 * belonging to it. Symmetric padding gives the line equal room above and below, which is what a
	 * divided list is meant to look like. The first and last sections are trimmed flush, so the rail
	 * doesn't open and close with a half-gap against the panel's own padding.
	 *
	 * Defaults to 0 — a rail of option-heavy sections (a facet rail) is already taller than its column,
	 * where extra height only buys more scrolling. Set it where the rail has room to spend.
	 */
	sectionPadding?: number;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * Renders a filter rail from a declarative `sections` → `fields` config: the controls, their labels,
 * their grouping, their gating, their test ids, and their loading skeleton.
 *
 * Use it when a rail is a list of filters (most rails are). Reach for the pieces —
 * `FilterFieldGroup`, `FilterFieldRow`, `FacetFilterSection` — when a rail needs a layout this
 * doesn't express, and use a `custom` field for a control the kinds don't cover.
 *
 * Owns no filter state: the page holds the values and the handlers, exactly as with the primitives.
 * Goes inside `FilterSidebar`, which supplies the panel, the title, "Clear all" and the collapse.
 */
export const FilterFieldList: FC<FilterFieldListProps> = ({
	sections,
	loading = false,
	dividers = false,
	gap,
	fieldGap,
	// DEFAULT: flush — see the prop's doc; a tall facet rail shouldn't pay for air it has no room for.
	sectionPadding = 0,
	sx,
	dataTestId,
}) => {
	// DEFAULT: 0 under `dividers` (the line separates the groups), else the rail's field rhythm.
	const resolvedGap = gap ?? (dividers ? 0 : DEFAULT_GAP);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: resolvedGap,
				...(dividers ? { "& > * + *": { borderTop: `1px solid ${NEUTRAL[150]}` } } : {}),
				...(sectionPadding > 0 ? { "& > *": { py: sectionPadding }, "& > *:first-of-type": { pt: 0 }, "& > *:last-of-type": { pb: 0 } } : {}),
				...sx,
			}}
			data-testid={dataTestId}
		>
			{sections.filter(isVisible).map((section) => {
				const visibleFields = section.fields.filter(isVisible);
				// A section whose every field is gated off would still contribute its title, frame and gap.
				if (visibleFields.length === 0) return null;
				return (
					<FilterFieldGroup
						key={section.id}
						title={section.title}
						frame={section.frame}
						titleVariant={section.titleVariant}
						collapsible={section.collapsible}
						defaultExpanded={section.defaultExpanded}
						activeCount={section.activeCount}
						fieldGap={section.fieldGap ?? fieldGap}
						// Only a TITLED section renders chrome of its own and is worth addressing. An untitled
						// section is a pure layout stack, and claiming an id there put `team-filter-status` on
						// both this wrapper and the facet nested inside it — the same id on two nodes.
						dataTestId={dataTestId && section.title ? `${dataTestId}-${section.id}` : undefined}
					>
						{visibleFields.map((field) =>
							loading ? <FilterFieldSkeleton key={field.id} field={field} /> : <FilterFieldControl key={field.id} field={field} dataTestId={dataTestId} />,
						)}
					</FilterFieldGroup>
				);
			})}
		</Box>
	);
};
